import { create } from 'zustand'
import axios from 'axios'
import { cleanParamsFilter } from 'Utils/cleanParamsFilter'

import { CRUDBaseStore } from './Base/CRUD'

interface ShippingStore extends CRUDBaseStore {
  selectedId: any
  inventories: any[]
  setSelectedId: (selectedId: any) => void
  getOne: (id: number) => Promise<{ success: boolean; data: any }>
  getInventories: (id: number) => Promise<{ success: boolean; data: any }>
  downloadAttachFile: (id: number) => Promise<{ success: boolean; data: any }>
  uploadAttachFile: (id: number, file: File) => Promise<{ success: boolean; data: any }>
  editShippingItem: (id: number, data: any) => Promise<{ success: boolean; data: any }>
  scanShipping: (
    shippingId: number,
    inventoryCode: string
  ) => Promise<{ success: boolean; data: any }>
  scanShippingAll(shippingId: number): Promise<{ success: boolean; data: any }>
  deliverShipping(shippingId: number): Promise<{ success: boolean; data: any }>
}

export const API_SLUG = 'shipping'

export const INITIAL_STATE = {
  startShippingAt: null,
  endShippingAt: null,
  pilePerRound: '',
  driverFullName: '',
  deliveryTime: '',
  deliveryBy: '',
  vehicleNo: '',
  transferDocumentNo: '',
  shippingItems: [] as any[],
}

export const useShippingStore = create<ShippingStore>(set => ({
  data: [],
  selected: INITIAL_STATE,
  selectedId: undefined,
  inventories: [],
  loading: false,
  error: undefined,
  setLoading: (loading: boolean) => {
    set({ loading })
  },
  setError: (error: any) => {
    set({ error })
  },
  setSelected: (selected: any) => {
    set({ selected })
  },
  setSelectedId: (selectedId: any) => {
    set({ selectedId })
  },
  getAll: async (filter: any) => {
    set({ loading: true })
    const finaleFilter = cleanParamsFilter(filter)

    return axios
      .get(`/${API_SLUG}`, { params: finaleFilter })
      .then(res => {
        set({ data: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getOne: async id => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/${id}`)
      .then(res => {
        set({ selected: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getInventories: async (id: any) => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/${id}/inventories`)
      .then(res => {
        set({ inventories: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  downloadAttachFile: async (id: number) => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/${id}/download-file`, { responseType: 'blob' })
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  uploadAttachFile: async (id: number, file: File) => {
    set({ loading: true })

    const formData = new FormData()
    formData.append('file', file)

    return axios
      .post(`/${API_SLUG}/${id}/upload-file`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  create: async (data: any) => {
    set({ loading: true })

    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        if (key === 'shippingItems') {
          data[key].forEach((piles: any, idx: number) => {
            formData.append(`piles[${idx}][pileTitle]`, piles.itemTitle)
            formData.append(`piles[${idx}][pileCode]`, piles.itemCode)
            formData.append(`piles[${idx}][deliverQuantity]`, piles.deliverQuantity)
            formData.append(`piles[${idx}][saleOrderItemId]`, piles.saleOrderItemId)
          })
        } else {
          formData.append(key, data[key])
        }
      }
    })

    return axios
      .post(`/${API_SLUG}`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  edit: async (id, data) => {
    set({ loading: true })

    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        if (key === 'shippingItems') {
          data[key].forEach((piles: any, idx: number) => {
            // if (piles.id) {
            //   formData.append(`piles[${idx}][id]`, piles.id)
            // }
            if (piles.isDelete) {
              formData.append(`piles[${idx}][isDelete]`, piles.isDelete)
            }

            formData.append(`piles[${idx}][deliverQuantity]`, piles.deliverQuantity ?? '')
            formData.append(`piles[${idx}][pileTitle]`, piles.itemTitle)
            formData.append(`piles[${idx}][pileCode]`, piles.itemCode)
          })
        } else {
          formData.append(key, data[key])
        }
      }
    })

    return axios
      .patch(`/${API_SLUG}/${id}`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  editShippingItem: async (id, data) => {
    set({ loading: true })

    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        if (key === 'shippingItems') {
          data[key].forEach((piles: any, idx: number) => {
            // if (piles.id) {
            //   formData.append(`piles[${idx}][id]`, piles.id)
            // }
            if (piles.isDelete) {
              formData.append(`piles[${idx}][isDelete]`, piles.isDelete)
            }

            formData.append(`piles[${idx}][deliverQuantity]`, piles.deliverQuantity ?? '')
            formData.append(`piles[${idx}][pileTitle]`, piles.itemTitle)
            formData.append(`piles[${idx}][pileCode]`, piles.itemCode)
          })
        } else {
          // formData.append(key, data[key])
        }
      }
    })

    return axios
      .patch(`/${API_SLUG}/${id}/shipping-items`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },

  delete: async id => {
    set({ loading: true })
    return axios
      .delete(`/${API_SLUG}/${id}`)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  scanShipping: async (shippingId: number, inventoryCode: string) => {
    set({ loading: true })
    const formData = new FormData()
    formData.append(`InventoryCode`, inventoryCode)
    return axios
      .post(`/${API_SLUG}/${shippingId}/scan-shipping`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: null }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  scanShippingAll: async (shippingId: number) => {
    set({ loading: true })

    return axios
      .post(`/${API_SLUG}/${shippingId}/scan-shipping/all`)
      .then(res => {
        set({ loading: false })
        return { success: true, data: null }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  deliverShipping: async (shippingId: number) => {
    set({ loading: true })

    return axios
      .post(`/${API_SLUG}/${shippingId}/deliver`)
      .then(res => {
        set({ loading: false })
        return { success: true, data: null }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  clearState: () => {
    set({
      data: [],
      loading: false,
      error: undefined,
      selected: INITIAL_STATE,
      selectedId: undefined,
    })
  },
}))
